<template>
  <div class="destinations-page page-container">
    <Breadcrumbs :breadcrumbs="breadcrumbs" class="mt-4"/>

    <h1>{{ $t('Experiences by Destination') }}</h1>
    <p class="destinations-subtitle">
      {{ $t('Browse experiences, tours and activities across countries and cities worldwide.') }}
    </p>

    <div v-if="loading" class="destinations-loading">
      <ProgressBar :value="50" :show-value="false" />
    </div>

    <div v-else-if="countries.length" class="destinations-grid">
      <nuxt-link
        v-for="country in countries"
        :key="country.country_code"
        :to="localePath(`/experiences/destinations/${country.slug}`)"
        class="destination-card"
      >
        <div class="destination-card__header">
          <span class="destination-card__flag">{{ getFlag(country.country_code) }}</span>
          <h2 class="destination-card__name">{{ country.country_name }}</h2>
        </div>
        <div class="destination-card__count">
          {{ country.activity_count }} {{ $t('experiences') }}
        </div>
        <div v-if="country.top_cities?.length" class="destination-card__cities">
          <span v-for="(city, i) in country.top_cities.slice(0, 3)" :key="city.slug" class="destination-card__city">
            {{ city.name }}<span v-if="i < Math.min(country.top_cities.length, 3) - 1">, </span>
          </span>
        </div>
      </nuxt-link>
    </div>

    <div v-else class="destinations-empty">
      {{ $t('No destinations available at this time.') }}
    </div>

    <div class="cross-link-section">
      <nuxt-link :to="localePath('/hotels/destinations')" class="cross-link">
        {{ $t('Browse hotel destinations') }}
      </nuxt-link>
    </div>
  </div>
</template>

<script setup lang="ts">
import ProgressBar from 'primevue/progressbar'
import { useHreflangTags, useBreadcrumbSchema } from '~/composables/useSeo'

const config = useRuntimeConfig()
const { $siteConfig } = useNuxtApp()
const localePath = useLocalePath()
const runtimeConfig = useRuntimeConfig()

const brand = computed(() => $siteConfig?.value?.brand || 'Tripening')
const baseUrl = config.public.baseURL

const breadcrumbs = [
  { label: 'Home', url: '/' },
  { label: 'Experiences', url: '/experiences' },
  { label: 'Destinations' }
]

interface City {
  name: string
  slug: string
  activity_count: number
}

interface Country {
  country_code: string
  country_name: string
  slug: string
  activity_count: number
  top_cities: City[]
}

const { data: _countries } = await useAsyncData(
  'experience-destinations',
  () => $fetch<Country[]>(`${baseUrl}/api/product/v1/experiences/destinations`).catch(() => [])
)
const countries = computed(() => _countries.value ?? [])
const loading = ref(false)

function getFlag(countryCode: string): string {
  try {
    return countryCode
      .toUpperCase()
      .split('')
      .map(c => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
      .join('')
  } catch {
    return ''
  }
}

// SEO
useHead({
  title: computed(() => `Experiences by Destination - Find Activities Worldwide | ${brand.value}`),
  link: [{ rel: 'canonical', href: `${baseUrl}/experiences/destinations` }],
  meta: [
    { name: 'description', content: computed(() => `Browse experiences by country and city on ${brand.value}. Find tours, activities and things to do worldwide.`) },
    { property: 'og:title', content: computed(() => `Experiences by Destination | ${brand.value}`) },
    { property: 'og:description', content: computed(() => `Browse experiences by country and city. Find tours and activities worldwide on ${brand.value}.`) },
    { property: 'og:url', content: `${baseUrl}/experiences/destinations` },
    { property: 'og:type', content: 'website' },
    { name: 'twitter:card', content: 'summary_large_image' },
  ]
})

useHreflangTags('/experiences/destinations')

useJsonld(() => useBreadcrumbSchema(breadcrumbs, runtimeConfig.public.baseURL))

useJsonld(() => {
  if (!countries.value.length) return null
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': 'Experience Destinations',
    'numberOfItems': countries.value.length,
    'itemListElement': countries.value.slice(0, 30).map((country, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      'name': country.country_name,
      'url': `${baseUrl}/experiences/destinations/${country.slug}`
    }))
  }
})

definePageMeta({
  layout: 'search'
})
</script>

<style scoped>
.destinations-page {
  padding-bottom: 60px;
}

.destinations-page h1 {
  font-size: 28px;
  font-weight: 700;
  margin-top: 16px;
  margin-bottom: 8px;
}

.destinations-subtitle {
  font-size: 16px;
  color: var(--dark-grey);
  margin-bottom: 32px;
}

.destinations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.destination-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border: 1px solid var(--light-grey-border, #e0e0e0);
  border-radius: 12px;
  transition: box-shadow 0.2s, transform 0.2s;
  text-decoration: none;
  color: inherit;
}

.destination-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.destination-card__header {
  display: flex;
  align-items: center;
  gap: 10px;
}

.destination-card__flag {
  font-size: 28px;
  line-height: 1;
}

.destination-card__name {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.destination-card__count {
  font-size: 14px;
  color: var(--blue, #1985f9);
  font-weight: 500;
}

.destination-card__cities {
  font-size: 13px;
  color: var(--dark-grey);
}

.destinations-loading {
  padding: 40px 0;
}

.destinations-empty {
  text-align: center;
  padding: 60px 0;
  color: var(--dark-grey);
  font-size: 16px;
}

.cross-link-section {
  margin-top: 40px;
  padding-top: 20px;
  border-top: 1px solid var(--light-grey-border, #e0e0e0);
}

.cross-link {
  color: var(--blue, #1985f9);
  font-weight: 500;
  text-decoration: none;
}

.cross-link:hover {
  text-decoration: underline;
}

@media screen and (max-width: 768px) {
  .destinations-grid {
    grid-template-columns: 1fr;
  }

  .destinations-page h1 {
    font-size: 22px;
  }
}
</style>
